// Schrittanzeige der Schadenmeldung: Fortschrittsbalken plus die Schritte als
// EINE Reihe. Passen nicht alle in die Breite (Telefon), werden nur so viele
// gezeigt, wie ohne Zeilenumbruch hineinpassen — ein Fenster um den aktuellen
// Schritt (11.10.2026; vorher brach die Reihe auf drei, vier Zeilen um).

import * as React from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { FONT_BODY } from "./theme";

export interface StepIndicatorStep {
  key: string;
  label: string;
}

interface Props {
  steps: StepIndicatorStep[];
  currentIndex: number;
  visitedCount: number;
  onJump?: (index: number) => void;
}

const GAP = 6;

/**
 * Größtes zusammenhängendes Fenster um `aktuell`, das in `breite` passt.
 * Wächst abwechselnd nach vorn und nach hinten — nach vorn zuerst, weil man
 * sehen will, was als Nächstes kommt.
 */
export function sichtbaresFenster(
  breiten: number[],
  aktuell: number,
  breite: number,
  gap = GAP,
): [number, number] {
  const n = breiten.length;
  if (n === 0) return [0, -1];
  const cur = Math.min(Math.max(aktuell, 0), n - 1);
  let lo = cur;
  let hi = cur;
  let summe = breiten[cur];
  let wuchs = true;
  while (wuchs) {
    wuchs = false;
    if (hi + 1 < n && summe + gap + breiten[hi + 1] <= breite) {
      hi += 1;
      summe += gap + breiten[hi];
      wuchs = true;
    }
    if (lo - 1 >= 0 && summe + gap + breiten[lo - 1] <= breite) {
      lo -= 1;
      summe += gap + breiten[lo];
      wuchs = true;
    }
  }
  return [lo, hi];
}

const useStyles = makeStyles({
  wrap: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  trackRow: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: `${GAP}px`,
    flexWrap: "nowrap",
    overflow: "hidden",
    // Luft für den Schatten des aktiven Schritts — sonst schneidet overflow ihn ab.
    paddingTop: "4px",
    paddingBottom: "10px",
  },
  /** Unsichtbare Messreihe mit ALLEN Schritten — liefert die Breite je Schritt. */
  messReihe: {
    position: "absolute",
    top: 0,
    left: 0,
    display: "flex",
    gap: `${GAP}px`,
    visibility: "hidden",
    pointerEvents: "none",
    whiteSpace: "nowrap",
  },
  pip: {
    appearance: "none",
    backgroundColor: "transparent",
    color: "var(--sm-on-dark-muted)",
    fontFamily: FONT_BODY,
    fontSize: "0.78rem",
    fontWeight: 600,
    letterSpacing: "0.02em",
    cursor: "default",
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "12px",
    paddingRight: "12px",
    whiteSpace: "nowrap",
    flexShrink: 0,
    ...shorthands.border("1px", "solid", "transparent"),
    ...shorthands.borderRadius("999px"),
    transitionProperty: "color, background-color, border-color, transform",
    transitionDuration: "180ms",
  },
  pipVisited: {
    color: "var(--sm-on-dark)",
    cursor: "pointer",
    ...shorthands.border("1px", "solid", "rgba(255,255,255,0.18)"),
    ":hover": {
      backgroundColor: "rgba(255,255,255,0.06)",
    },
  },
  pipActive: {
    color: "var(--sm-primary-deep)",
    backgroundColor: "var(--sm-accent)",
    ...shorthands.border("1px", "solid", "var(--sm-accent)"),
    boxShadow: "0 6px 18px rgba(247,168,35,0.35)",
  },
  progressTrack: {
    height: "6px",
    width: "100%",
    backgroundColor: "rgba(255,255,255,0.08)",
    ...shorthands.borderRadius("999px"),
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    backgroundImage:
      "linear-gradient(90deg, var(--sm-accent) 0%, #ffce6e 100%)",
    transitionProperty: "width",
    transitionDuration: "320ms",
    ...shorthands.borderRadius("999px"),
  },
  meta: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    color: "var(--sm-on-dark-muted)",
    fontFamily: FONT_BODY,
    fontSize: "0.78rem",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },
});

export default function StepIndicator({
  steps,
  currentIndex,
  visitedCount,
  onJump,
}: Props) {
  const styles = useStyles();
  const reiheRef = React.useRef<HTMLDivElement | null>(null);
  const messRef = React.useRef<HTMLDivElement | null>(null);
  const [fenster, setFenster] = React.useState<[number, number]>([0, steps.length - 1]);
  const labelsKey = steps.map((s) => s.label).join("|");

  React.useLayoutEffect(() => {
    const reihe = reiheRef.current;
    const mess = messRef.current;
    if (!reihe || !mess) return;
    const messen = () => {
      const breiten = Array.from(mess.children).map((el) => (el as HTMLElement).offsetWidth);
      const verfuegbar = reihe.clientWidth;
      // Ohne Layout (jsdom, ausgeblendet) alles zeigen statt nichts.
      if (!verfuegbar || breiten.every((b) => b === 0)) {
        setFenster([0, steps.length - 1]);
        return;
      }
      setFenster(sichtbaresFenster(breiten, currentIndex, verfuegbar));
    };
    messen();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(messen);
    ro.observe(reihe);
    return () => ro.disconnect();
  }, [labelsKey, currentIndex, steps.length]);

  const pipClass = (idx: number) => {
    const isActive = idx === currentIndex;
    const isVisited = idx <= visitedCount && !isActive;
    return {
      isActive,
      isVisited,
      cls: mergeClasses(
        styles.pip,
        isVisited ? styles.pipVisited : "",
        isActive ? styles.pipActive : "",
      ),
    };
  };

  const progress =
    steps.length <= 1
      ? 0
      : Math.max(0, Math.min(1, currentIndex / (steps.length - 1)));
  return (
    <div className={styles.wrap}>
      <div className={styles.meta}>
        <span>
          Schritt {Math.min(currentIndex + 1, steps.length)} von {steps.length}
        </span>
        <span>{steps[currentIndex]?.label ?? ""}</span>
      </div>
      <div className={styles.progressTrack}>
        <div
          className={styles.progressFill}
          style={{ width: `${progress * 100}%` }}
        />
      </div>
      <div className={styles.trackRow} ref={reiheRef}>
        <div className={styles.messReihe} ref={messRef} aria-hidden>
          {steps.map((step, idx) => (
            <span key={step.key} className={pipClass(idx).cls}>
              {step.label}
            </span>
          ))}
        </div>
        {steps.map((step, idx) => {
          if (idx < fenster[0] || idx > fenster[1]) return null;
          const { isActive, isVisited, cls } = pipClass(idx);
          return (
            <button
              key={step.key}
              type="button"
              className={cls}
              disabled={!isVisited && !isActive}
              onClick={() => {
                if (isVisited && onJump) onJump(idx);
              }}
            >
              {step.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
